import { useEffect, useState } from 'react';

/**
 * Multi-monitor pop-out support (Phase V).
 *
 * An operator can pop a single camera out into its own resizable window and
 * drag it to another monitor. The main map keeps a live registry of which
 * cameras currently have an open pop-out so it can pulse those markers.
 *
 * Sync uses a same-origin BroadcastChannel: each pop-out window announces
 * `open` on mount and `close` on unload; the main window listens and toggles a
 * Set of camera ids. (A BroadcastChannel never delivers a message to the
 * instance that posted it, so the main window relies on the pop-out's own
 * broadcasts rather than self-posting when it spawns the window.)
 */

export const POPOUT_CHANNEL_NAME = 'tsr-popout';

export interface PopoutMessage {
  type: 'open' | 'close';
  cameraId: string;
}

/** Open (or focus, if already open) a pop-out window for a camera. The window
 *  name is keyed by camera id so re-clicking focuses the existing window rather
 *  than spawning a duplicate. Returns null if the browser blocked the pop-up. */
export function openPopoutWindow(cameraId: string): Window | null {
  return window.open(`/popout#cam=${cameraId}`, `tsr-popout-${cameraId}`, 'width=800,height=600');
}

/** Pure reducer: fold a pop-out message into the open-id set. Returns a new Set
 *  (never mutates) so React state updates trigger a re-render. */
export function applyPopoutMessage(prev: ReadonlySet<string>, msg: PopoutMessage): Set<string> {
  const next = new Set(prev);
  if (msg.type === 'open') next.add(msg.cameraId);
  else next.delete(msg.cameraId);
  return next;
}

function isPopoutMessage(data: unknown): data is PopoutMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return (m.type === 'open' || m.type === 'close') && typeof m.cameraId === 'string';
}

/**
 * Main-window hook: returns the set of camera ids that currently have an open
 * pop-out, kept in sync via the BroadcastChannel. Empty (and inert) in
 * environments without BroadcastChannel.
 */
export function usePopoutRegistry(): ReadonlySet<string> {
  const [ids, setIds] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(POPOUT_CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      if (isPopoutMessage(e.data)) setIds((prev) => applyPopoutMessage(prev, e.data));
    };
    return () => channel.close();
  }, []);
  return ids;
}

/**
 * Pop-out-window side-effect: announce `open` for this camera on mount and
 * `close` on unmount / beforeunload, so the main window can pulse / un-pulse
 * the marker. Returns a cleanup that the caller wires into useEffect.
 */
export function announcePopout(cameraId: string): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => undefined;
  const channel = new BroadcastChannel(POPOUT_CHANNEL_NAME);
  channel.postMessage({ type: 'open', cameraId } satisfies PopoutMessage);
  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    channel.postMessage({ type: 'close', cameraId } satisfies PopoutMessage);
    channel.close();
  };
  // beforeunload covers the operator closing the window via the OS chrome
  // (where React unmount cleanup never runs).
  window.addEventListener('beforeunload', close);
  return () => {
    window.removeEventListener('beforeunload', close);
    close();
  };
}
